import React, { useEffect, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ConfirmationModal from "../components/Modals/ConfirmationModal";
import {
  RestorableWorkspace,
  useRestorableWorkspaces,
  useWorkspaceRestore,
} from "../services/api";

type RestoreError = { status?: number; message?: string };

export const restoreErrorMessage = (error: RestoreError): string => {
  if (error.status === 403)
    return "You do not have permission to restore this workspace.";
  if (error.status === 410)
    return "The 30-day restore period has expired. This workspace cannot be restored.";
  if (error.status === 409) {
    if (error.message?.includes("Free a workspace slot"))
      return "Free a workspace slot before restoring this workspace.";
    if (error.message?.includes("purge has started"))
      return "This workspace cannot be restored because permanent removal has started.";
    if (error.message?.includes("no accepted owner"))
      return "This workspace cannot be restored because it has no accepted owner.";
    if (error.message?.includes("not enrolled"))
      return "This workspace cannot be restored because it is not enrolled in the 30-day recovery period.";
    return "This workspace cannot be restored right now. Try again later.";
  }
  if (error.status === 404)
    return "This workspace is no longer available for restoration.";
  return error.message || "Unable to restore this workspace. Try again.";
};

const utcDeadline = (value: string): string => {
  // The API serializes a UTC database timestamp without a timezone suffix.
  const timestamp = /(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`;
  return new Date(timestamp).toUTCString();
};

type Props = { refreshKey: number; onRestored: () => Promise<void> };

const RecentlyDeletedWorkspaces: React.FC<Props> = ({
  refreshKey,
  onRestored,
}) => {
  const list = useRestorableWorkspaces();
  const restore = useWorkspaceRestore();
  const [workspaces, setWorkspaces] = useState<RestorableWorkspace[]>([]);
  const [selected, setSelected] = useState<RestorableWorkspace | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const refresh = async () => {
    try {
      setWorkspaces(await list());
      setListError(false);
    } catch {
      setListError(true);
    }
  };

  useEffect(() => {
    void refresh();
    // The API hook returns a new function each render. A deletion changes refreshKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleRestore = async () => {
    if (!selected) return;
    setRestoring(true);
    setError(null);
    try {
      await restore(selected.id);
      setWorkspaces((current) =>
        current.filter((workspace) => workspace.id !== selected.id),
      );
      setSelected(null);
      await onRestored();
      await refresh();
    } catch (reason) {
      setError(restoreErrorMessage(reason as RestoreError));
    } finally {
      setRestoring(false);
    }
  };

  if (!workspaces.length && !listError) return null;
  return (
    <Card>
      <Accordion
        expanded={expanded}
        onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
      >
        <AccordionSummary
          indicator={
            expanded ? (
              <RemoveIcon data-testid="recently-deleted-collapse-icon" />
            ) : (
              <AddIcon data-testid="recently-deleted-expand-icon" />
            )
          }
        >
          <Typography level="h3">Recently deleted</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            {listError && (
              <Alert color="danger" role="alert">
                Could not load recently deleted workspaces.
              </Alert>
            )}
            <Stack spacing={1}>
              {workspaces.map((workspace) => (
                <Box
                  key={workspace.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography level="title-md">{workspace.name}</Typography>
                    <Typography level="body-sm">
                      Permanently removed after{" "}
                      {utcDeadline(workspace.retention_expires_at)}
                    </Typography>
                  </Box>
                  <Button
                    size="sm"
                    variant="outlined"
                    aria-label={`Restore ${workspace.name}`}
                    onClick={() => {
                      setSelected(workspace);
                      setError(null);
                    }}
                  >
                    Restore
                  </Button>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </AccordionDetails>
      </Accordion>
      <ConfirmationModal
        open={selected !== null}
        title={`Restore ${selected?.name ?? "workspace"}`}
        message="Restore this workspace and make it available to its members again?"
        actionLabel="Restore"
        onAction={handleRestore}
        onClose={() => {
          setSelected(null);
          setError(null);
        }}
        loading={restoring}
        error={error}
      />
    </Card>
  );
};

export default RecentlyDeletedWorkspaces;
